import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Share2, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { TICKET_SHOP_URL } from "@/lib/media";
import { Button } from "@/components/ui/button";

export default function ProductCard({ product, index = 0 }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);

  const name = lang === "de" ? product.name_de : product.name_en;
  const desc = lang === "de" ? product.desc_de : product.desc_en;
  const badge = lang === "de" ? product.badge_de : product.badge_en;

  const buy = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key: product.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Checkout fehlgeschlagen");
      setLoading(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    const shareData = { title: `Krampus-Show – ${name}`, text: desc, url };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (e) { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t("shared"));
    }
  };

  return (
    <motion.article
      data-testid={`product-card-${product.lookup_key}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group relative bg-hell-coal border border-white/10 overflow-hidden hover:border-hell-gold/40 hover:-translate-y-1 transition-all duration-500"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={product.image} alt={name} loading="lazy"
          className={`w-full h-full object-cover transition-all duration-700 ${product.sold_out ? "opacity-30 grayscale scale-100" : "group-hover:scale-105"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-hell-coal via-transparent to-transparent" />
        {product.sold_out ? (
          <span data-testid={`soldout-${product.lookup_key}`} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-6 border-2 border-hell-red bg-hell-black/80 text-hell-redhot font-display text-2xl tracking-[0.1em] uppercase px-5 py-2">
            {t("soldout")}
          </span>
        ) : badge && (
          <span className="absolute top-4 left-4 bg-hell-red/90 text-hell-bone font-cinzel text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
            {badge}
          </span>
        )}
        <button
          data-testid={`share-${product.lookup_key}`}
          onClick={share}
          aria-label={t("share")}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center text-hell-bone/80 hover:text-hell-gold hover:border-hell-gold/60 transition-colors"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl md:text-2xl text-hell-bone mb-2">{name}</h3>
        <p className="text-sm text-hell-smoke leading-relaxed mb-5 line-clamp-3">{desc}</p>
        {product.contact_only ? (
          <div className="flex flex-col gap-4">
            <p data-testid={`contact-${product.lookup_key}`} className="font-cinzel text-[13px] tracking-[0.08em] text-hell-gold leading-relaxed">
              {t("contact_us")}
            </p>
            <a href={`mailto:office@latschenwirt.at?subject=${encodeURIComponent(t(product.lookup_key === "ticket_vip_tisch" ? "mail_subj_vip_tisch" : "mail_subj_vip_sponsor"))}`}>
              <Button
                data-testid={`buy-${product.lookup_key}`}
                className="rounded-full font-cinzel tracking-[0.12em] uppercase text-[11px] px-6 transition-all duration-300 bg-hell-red hover:bg-hell-redhot text-hell-bone hover:shadow-[0_0_18px_rgba(139,0,0,0.6)]"
              >
                {t(product.lookup_key === "ticket_vip_tisch" ? "cta_vip_tisch" : "cta_vip_sponsor")}
              </Button>
            </a>
          </div>
        ) : (
        <div className="flex items-center justify-between gap-3">
          <span data-testid={`price-${product.lookup_key}`} className="font-cinzel text-xl text-hell-gold">
            {product.price_prefix ? `${t("from_price")} ` : ""}€ {product.price.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          {product.kind === "ticket" && !product.sold_out ? (
            <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid={`buy-${product.lookup_key}`}
                className="rounded-full font-cinzel tracking-[0.12em] uppercase text-[11px] px-5 transition-all duration-300 bg-hell-red hover:bg-hell-redhot text-hell-bone hover:shadow-[0_0_18px_rgba(139,0,0,0.6)]"
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-2" />
                {t("buy")}
              </Button>
            </a>
          ) : (
            <Button
              data-testid={`buy-${product.lookup_key}`}
              onClick={buy}
              disabled={loading || product.sold_out}
              className={`rounded-full font-cinzel tracking-[0.12em] uppercase text-[11px] px-5 transition-all duration-300 ${product.sold_out ? "bg-hell-coal border border-white/15 text-hell-smoke cursor-not-allowed" : "bg-hell-red hover:bg-hell-redhot text-hell-bone hover:shadow-[0_0_18px_rgba(139,0,0,0.6)]"}`}
            >
              <ShoppingCart className="w-3.5 h-3.5 mr-2" />
              {loading ? "…" : product.sold_out ? t("soldout") : t("buy")}
            </Button>
          )}
        </div>
        )}
      </div>
    </motion.article>
  );
}
